import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldBan, ShieldCheck, RotateCcw, Trash2, Archive, Clock } from "lucide-react";
import { api, formatError, avatarSrc, fmtDate, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { AdminHeader } from "../../components/AdminLayout";

const ROLE_LABEL = { guest: "Guest", student: "Student", vip: "VIP member", instructor: "Instructor", artist: "Artist", admin: "Admin", super_admin: "Super admin" };
const ROLE_COLOR = { super_admin: "#D4AF37", admin: "#9D4EDD", artist: "#F59E0B", instructor: "#5CC8FF", vip: "#EC4899", student: "#4ADE80", guest: "#A19DB0" };

const daysLeft = (deletedAt, retention) => {
  try {
    const gone = (Date.now() - new Date(deletedAt).getTime()) / 86400000;
    return Math.max(0, Math.ceil((retention || 180) - gone));
  } catch { return null; }
};

export default function AdminDeleted() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [rows, setRows] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/deleted-accounts").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleBlock = async (r) => {
    try {
      await api.post(`/admin/deleted-accounts/${r.id}/blocklist`, { blocked: !r.email_blocked });
      toast.success(r.email_blocked ? "Email un-blocked" : "Email blocked from re-use");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const restore = async (r) => {
    if (!window.confirm(`Restore ${r.name}'s account and all their data back to active members?`)) return;
    try { await api.post(`/admin/deleted-accounts/${r.id}/restore`); toast.success("Account restored"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const purge = async (r) => {
    if (!window.confirm(`Permanently delete ${r.name} and ALL their data now? This cannot be undone.`)) return;
    try { await api.delete(`/admin/deleted-accounts/${r.id}`); toast.success("Permanently deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <AdminHeader title="Deleted accounts" subtitle={rows ? `${rows.length} archived · kept for 6 months` : "Loading…"} />
      <div className="flex gap-2 mb-4" data-testid="deleted-subtabs">
        <Link to="/admin/members" data-testid="subtab-members" className="rounded-full px-4 py-1.5 text-sm font-medium bg-white/5 border border-white/10 text-[#A19DB0] hover:text-white">Members</Link>
        <span className="rounded-full px-4 py-1.5 text-sm font-semibold bg-[#D4AF37] text-black">Deleted accounts</span>
      </div>
      <div className="flex items-start gap-2 text-xs text-[#A19DB0] bg-white/5 border border-white/10 rounded-xl p-3 mb-5">
        <Archive size={15} className="text-[#D4AF37] mt-0.5 shrink-0" />
        <p>Deleted members are archived here with all their data for 6 months, then auto-removed. Block an email to stop it being used again. Only a super admin can restore or permanently delete.</p>
      </div>

      {rows === null ? (
        <p className="text-[#A19DB0] text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-[#A19DB0]" data-testid="deleted-empty">
          <Archive size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No deleted accounts.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const dl = daysLeft(r.deleted_at, r.retention_days);
            return (
              <div key={r.id} data-testid={`deleted-row-${r.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-4">
                <img src={avatarSrc(r)} alt="" className="w-11 h-11 rounded-full object-cover border border-white/10 opacity-70" />
                <div className="flex-1 min-w-[160px]">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{r.name}</p>
                    {r.email_blocked && <span className="text-[10px] uppercase bg-[#E57373]/20 text-[#E57373] px-2 py-0.5 rounded-full">Blocked</span>}
                  </div>
                  <p className="text-[#A19DB0] text-xs truncate">{r.email}</p>
                  <p className="text-[#A19DB0]/70 text-[11px] mt-0.5">Deleted {fmtDateTime(r.deleted_at)}{r.deleted_by_name ? ` · by ${r.deleted_by_name}` : ""}</p>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: `${ROLE_COLOR[r.role] || "#A19DB0"}22`, color: ROLE_COLOR[r.role] || "#A19DB0" }}>{ROLE_LABEL[r.role] || r.role}</span>
                {dl !== null && <span className="flex items-center gap-1 text-[11px] text-[#A19DB0]"><Clock size={12} /> {dl}d left</span>}
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleBlock(r)} data-testid={`deleted-block-${r.id}`} title={r.email_blocked ? "Un-block email" : "Block email from re-use"} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10">
                    {r.email_blocked ? <ShieldCheck size={16} className="text-[#4ADE80]" /> : <ShieldBan size={16} className="text-[#E5A84B]" />}
                  </button>
                  {isSuper && (
                    <>
                      <button onClick={() => restore(r)} data-testid={`deleted-restore-${r.id}`} title="Restore account" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><RotateCcw size={16} className="text-[#9D4EDD]" /></button>
                      <button onClick={() => purge(r)} data-testid={`deleted-purge-${r.id}`} title="Permanently delete" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><Trash2 size={16} className="text-[#E57373]" /></button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
